// #2121 v2 - Modal de asignación / re-derivación de uno o varios estudios remote-device.
// Si selectedReportId es array (>1), usa el endpoint bulk; si es string, usa el endpoint single.
import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, FormGroup, Label, Input, Alert, Spinner, Collapse,
} from 'reactstrap';
import Select from 'react-select';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'react-translate';
import {
  actionCloseAssignModal,
  actionReassignStudy,
  actionBulkReassign,
} from 'store/actions/deviceStudiesActions';

const AssignDoctorModal = () => {
  const dispatch = useDispatch();
  const t        = useTranslate('device-studies');

  const [selected, setSelected]       = useState(null);
  const [note, setNote]               = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);

  const {
    modalOpen,
    selectedReportId,
    assignableDoctors,
    loadingAssignable,
    reassigning,
    reassignError,
    studies,
  } = useSelector((s) => s.deviceStudiesReducer);

  const practice = useSelector((s) => s.practicesReducer.practice);

  // Limpiar campos al abrir
  useEffect(() => {
    if (modalOpen) {
      setSelected(null);
      setNote('');
      setHistoryOpen(false);
    }
  }, [modalOpen]);

  const targetIds = useMemo(() => {
    if (!selectedReportId) return [];
    return Array.isArray(selectedReportId) ? selectedReportId : [selectedReportId];
  }, [selectedReportId]);

  const isBulk = targetIds.length > 1;

  // Para mostrar contexto: pacientes y posibles re-derivaciones
  const targetStudies = useMemo(() => {
    return targetIds
      .map((id) => studies.find((s) => String(s._id) === String(id)))
      .filter(Boolean);
  }, [targetIds, studies]);

  const anyAlreadyAssigned = targetStudies.some(
    (s) => s.derivations && s.derivations.length > 0
  );
  const titleKey = isBulk
    ? 'modal-bulk-title'
    : (anyAlreadyAssigned ? 'modal-rederive-title' : 'modal-title');

  const onClose = () => {
    if (!reassigning) {
      setSelected(null);
      setNote('');
      dispatch(actionCloseAssignModal());
    }
  };

  const handleAssign = () => {
    if (!selected) return;
    if (isBulk) {
      dispatch(actionBulkReassign(targetIds, selected.value, practice?._id, note || null));
    } else {
      dispatch(actionReassignStudy(targetIds[0], selected.value, practice?._id, note || null));
    }
  };

  const options = assignableDoctors.map((d) => ({
    value: d.auth_user_id,
    label: `${d.firstName} ${d.lastName}${d.email ? ` — ${d.email}` : ''}`,
  }));

  // Para mostrar nombres en el historial inline
  const doctorNameMap = useMemo(() => {
    const m = {};
    (assignableDoctors || []).forEach((d) => { m[String(d.auth_user_id)] = `${d.firstName} ${d.lastName}`; });
    return m;
  }, [assignableDoctors]);

  // Historial inline solo en re-derivación single (en bulk no tiene sentido mezclar historiales de N estudios)
  const showHistoryToggle = !isBulk && anyAlreadyAssigned;
  const derivations = showHistoryToggle ? (targetStudies[0]?.derivations || []) : [];

  return (
    <Modal isOpen={modalOpen} toggle={onClose} autoFocus size={isBulk ? 'lg' : undefined}>
      <ModalHeader toggle={onClose}>{t(titleKey)}</ModalHeader>
      <ModalBody>
        {reassignError === '409' && (
          <Alert color="warning">{t('toast-error-conflict')}</Alert>
        )}
        {reassignError === '422' && (
          <Alert color="danger">{t('toast-error-422')}</Alert>
        )}

        {/* Resumen de estudios afectados */}
        {targetStudies.length > 0 && (
          <div className="mb-3">
            {isBulk ? (
              <>
                <Label className="text-muted small">{t('modal-bulk-summary').replace('{n}', targetStudies.length)}</Label>
                <ul className="list-unstyled mb-0" style={{ maxHeight: 150, overflowY: 'auto' }}>
                  {targetStudies.map((s) => (
                    <li key={s._id} className="small">
                      • {s.patient_data?.lastName}, {s.patient_data?.firstName}
                      {s.device_type && ` — ${s.device_type}`}
                      {s.derivations?.length > 0 && (
                        <span className="text-warning"> · {t('summary-already-assigned')}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                <Label className="text-muted small">{t('modal-single-summary')}</Label>
                <div>
                  <strong>{targetStudies[0].patient_data?.lastName}, {targetStudies[0].patient_data?.firstName}</strong>
                  {targetStudies[0].device_type && ` — ${targetStudies[0].device_type}`}
                </div>
                {anyAlreadyAssigned && (
                  <div className="text-muted small">
                    {t('modal-currently-assigned')}: <strong>{targetStudies[0].assigned_doctor_name || '—'}</strong>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Historial inline (solo en re-derivación single) */}
        {showHistoryToggle && (
          <div className="mb-3">
            <Button
              color="link"
              size="sm"
              className="p-0"
              onClick={() => setHistoryOpen((x) => !x)}
              style={{ textDecoration: 'none' }}
            >
              {historyOpen ? '▾' : '▸'} {t('history-toggle').replace('{n}', derivations.length)}
            </Button>
            <Collapse isOpen={historyOpen}>
              <ul className="list-unstyled mt-2 mb-0" style={{ borderLeft: '2px solid #eee', paddingLeft: 12 }}>
                {derivations.map((d, i) => (
                  <li key={i} className="mb-2 small">
                    <div>
                      <strong>{doctorNameMap[String(d.doctor_id)] || d.doctor_id}</strong>
                    </div>
                    <div className="text-muted">
                      {t('history-by')}: {doctorNameMap[String(d.assigned_by)] || d.assigned_by}
                      {' · '}
                      {moment(d.assigned_at).format('DD.MM.YYYY HH:mm')}
                    </div>
                    {d.note && <div style={{ background: '#f7f8fa', padding: 4, borderRadius: 3, marginTop: 2 }}>{d.note}</div>}
                  </li>
                ))}
              </ul>
            </Collapse>
          </div>
        )}

        <FormGroup>
          <Label>{t('modal-doctor-label')}</Label>
          <Select
            options={options}
            isLoading={loadingAssignable}
            isSearchable
            value={selected}
            onChange={setSelected}
            placeholder={t('select-placeholder')}
            noOptionsMessage={() => t('no-doctors-in-practice')}
          />
        </FormGroup>
        <FormGroup>
          <Label>{t('modal-note-label')}</Label>
          <Input
            type="textarea"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('modal-note-placeholder')}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={reassigning}>
          {t('modal-cancel')}
        </Button>
        <Button color="primary" disabled={!selected || reassigning} onClick={handleAssign}>
          {reassigning ? <Spinner size="sm" /> : t(isBulk ? 'modal-confirm-bulk' : 'modal-confirm')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AssignDoctorModal;
// #2121 - End
